(function () {
  'use strict';

  /**
   * Paid-Media Attribution Capture
   * ===
   *
   * Persists GCLID + UTM values from the landing URL into a first-party
   * cookie so they survive navigation to the Request-a-Quote form.
   *
   * WHY THIS EXISTS
   * Gravity Forms dynamic population reads the URL of the page the FORM sits
   * on. Paid traffic that lands on a campaign page or the homepage and then
   * navigates to Request-a-Quote arrives with no query string, so the hidden
   * fields populate empty and the failure is silent. This module captures the
   * params on whatever page they first appear and hands them to PHP via a
   * cookie; national_attribution_value() in
   * inc/plugin-settings/gravity-forms-settings.php reads it when populating the
   * form fields. (An earlier draft of this comment pointed at inc/attribution.php
   * -- that file was never created.)
   *
   * WHY JS AND NOT PHP
   * Capture has to survive full-page caching. A server-side capture stops
   * working the moment a page cache sits in front of PHP, and does so
   * silently. Reading the cookie back is done in PHP (see inc/attribution.php)
   * because that hooks Gravity Forms' existing dynamic-population parameter
   * names and can't be defeated by script timing on the form page.
   *
   * PRECEDENCE
   * URL params always win over the cookie. This module only ever writes; the
   * PHP side prefers a live $_GET value and falls back to the cookie. So a
   * direct ad click to the form page is unaffected by anything stored here.
   *
   * TOUCH MODEL
   * Both first-touch and last-touch are stored. PHP reads last-touch by
   * default -- Google Ads offline conversion import expects the GCLID of the
   * click that drove the conversion, and it matches gtag's own behaviour. The
   * first-touch values are stored anyway so the decision stays reversible
   * without a rebuild and without data already lost: flip
   * NAC_ATTR_TOUCH_MODEL in wp-config.php. Confirm the intended model with
   * National's paid-media side (see README open items).
   *
   * Concatenated into build/site.js by gulp (assets/js/*.js). Deliberately
   * jQuery-free so it can be moved to the header later if capture needs to
   * run earlier than the footer bundle.
   */

  // Single cookie holding both touch payloads, JSON, URI-encoded.
  var COOKIE_NAME = 'nac_attr';

  // 90 days: matches Google's own _gcl_aw gclid cookie lifetime and the
  // maximum Google Ads click-through conversion window, so the cookie can't
  // expire before the window it exists to serve.
  var TTL_DAYS = 90;

  // The seven params Novito verifies on the Zoho Lead (GCL_ID + UTM_*).
  // These are URL parameter names, and must match the parameter names set on
  // the hidden fields in Gravity Forms admin.
  //
  // utm_id added 2026-09-10: the client's issue #8 brief lists it alongside the
  // other five UTM params. It is GA4's campaign ID parameter and is a plausible
  // source for the still-unresolved Campaign_Code mapping on the Zoho side --
  // confirm with Novito rather than assuming.
  var PARAMS = [
    'gclid',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_id',
    'utm_content',
    'utm_term'
  ];

  // Guard against a hostile or malformed query string bloating the cookie.
  // 512 is well clear of real gclid/utm lengths and well under the 4KB
  // per-cookie limit once seven values are JSON-encoded.
  var MAX_VALUE_LENGTH = 512;

  /**
   * Consent gate.
   *
   * These are marketing-category cookies.
   *
   * STATUS 2026-08-14: there is NO cookie consent mechanism on the site yet --
   * confirmed with the client. An earlier version of this comment referred to a
   * CMP behind a "Manage Cookie Preferences" footer link; that was inaccurate.
   * Capture therefore runs ungated today.
   *
   * When a CMP is introduced, this is the single place to wire it: return false
   * until the marketing category is granted and no capture happens. Also add
   * `nac_attr` to the CMP's cookie inventory with a category assigned -- it is
   * first-party and set by our own code, so an automated scan will NOT find it
   * the way it finds GA or Ads tags.
   *
   * Note the banner's MODEL materially changes attribution coverage: an opt-in
   * gate silently drops attribution for everyone who ignores or declines it,
   * while opt-out / notice-only keeps coverage roughly where it is now. Flag with
   * National's privacy contact before launch.
   */
  function consentGranted() {
    // Explicit opt-out escape hatch for debugging / QA.
    if (window.NAC_ATTR_DISABLED === true) {
      return false;
    }

    return true;
  }

  /**
   * Read a cookie by name. Returns '' when absent.
   */
  function readCookie(name) {
    var target = name + '=';
    var parts = document.cookie ? document.cookie.split(';') : [];

    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].replace(/^\s+/, '');
      if (part.indexOf(target) === 0) {
        return part.substring(target.length);
      }
    }

    return '';
  }

  /**
   * Write a first-party cookie, root path, expiring in `days`.
   *
   * SameSite=Lax so the cookie still arrives on a top-level cross-site
   * navigation -- which is exactly what an ad click is. Strict would drop it.
   * Secure only on https so local dev over http still works.
   */
  function writeCookie(name, value, days) {
    var expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));

    var cookie = name + '=' + value +
      ';expires=' + expires.toUTCString() +
      ';path=/' +
      ';SameSite=Lax';

    if (window.location.protocol === 'https:') {
      cookie += ';Secure';
    }

    document.cookie = cookie;
  }

  /**
   * Pull the tracked params out of the current URL.
   * Returns an object containing only the params actually present and
   * non-empty, so an absent param can never blank a stored one.
   */
  function readParamsFromUrl() {
    var found = {};

    if (!window.location.search) {
      return found;
    }

    var query = new URLSearchParams(window.location.search);

    for (var i = 0; i < PARAMS.length; i++) {
      var key = PARAMS[i];
      var value = query.get(key);

      if (value === null) {
        continue;
      }

      value = value.trim();

      if (value === '') {
        continue;
      }

      found[key] = value.substring(0, MAX_VALUE_LENGTH);
    }

    return found;
  }

  /**
   * Parse the stored payload. Any malformed value is discarded rather than
   * thrown -- a corrupt cookie should cost us this visit's attribution, not
   * break the page.
   */
  function readStore() {
    var raw = readCookie(COOKIE_NAME);

    if (!raw) {
      return null;
    }

    try {
      var parsed = JSON.parse(decodeURIComponent(raw));
      return (parsed && typeof parsed === 'object') ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Capture.
   *
   * Only runs when the current URL actually carries attribution params, so
   * an ordinary pageview never rewrites the cookie and never refreshes its
   * expiry off the back of non-paid traffic.
   *
   * first  -- written once, never overwritten.
   * last   -- overwritten on every new attribution-bearing landing.
   */
  function capture() {
    if (!consentGranted()) {
      return;
    }

    // URLSearchParams is unavailable on genuinely ancient browsers; skip
    // rather than throw. Those visits lose attribution, nothing else.
    if (typeof URLSearchParams === 'undefined') {
      return;
    }

    var incoming = readParamsFromUrl();

    if (Object.keys(incoming).length === 0) {
      return;
    }

    var now = new Date().toISOString();
    var store = readStore() || {};

    if (!store.first || typeof store.first !== 'object') {
      store.first = incoming;
      store.first_ts = now;
    }

    store.last = incoming;
    store.last_ts = now;

    try {
      writeCookie(COOKIE_NAME, encodeURIComponent(JSON.stringify(store)), TTL_DAYS);
    } catch (e) {
      // Cookies disabled or quota hit. Nothing useful to do; PHP will fall
      // back to whatever is in the URL at submission time.
    }
  }

  capture();

})();
